import { FC, ReactNode, useContext, useEffect } from 'react';
import PageLayout from '../../layout/PageLayout/PageLayout';
import Card from '../../components/Card/Card';
import { IconMailWoosh, IconCircleExclamationMinimal } from '../../icons/Icons';
import common from '../../util/scss/common.module.scss';
import styles from './ForgotPasswordStaffScreen.module.scss';
import API from '../../api/api';
import ForgotPasswordStaffContext, {
    ForgotPasswordStaffProvider,
    EForgotPasswordStaffUiState,
} from '../../contexts/ForgotPasswordStaffContext';
import GlobalContext from '../../contexts/GlobalContext';
import CardLoader from '../../components/Loader/Loader';
import { Redirect } from 'react-router';
import { ERoutes } from '../../util/constants';
import { Alert, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

const ForgotPasswordStaffForm: FC = () => {
    const { language } = useContext(GlobalContext);
    const { email, setEmail, emailIsValid, forgotPasswordStaffUiState, submitForgotPasswordForm, errorMessage } =
        useContext(ForgotPasswordStaffContext);

    const handleFormSubmit = (event: React.SyntheticEvent) => {
        event.preventDefault();
        API.post.trackEvent({ event: 'Sign In || Staff Forgot Password | Button - Send link' });
        submitForgotPasswordForm();
    };

    const defaultHeader: ReactNode = (
        <header>
            <h1 className={`${common.pageHeader} ${common.withSubhead}`}>{language.forgotPassword.header}</h1>
            <h2 className={common.pageSubhead}>{language.forgotPassword.subheader}</h2>
        </header>
    );

    const retryHeader: ReactNode = (
        <>
            <section className={`${common.icon} ${styles.iconExclamation}`}>
                <IconCircleExclamationMinimal width={'65'} height={'65'} fillColor={'#767982'} />
            </section>
            <header>
                <h1 className={`${common.pageHeader} ${common.withSubhead}`}>
                    {language.forgotPassword.linkExpired.header}
                </h1>
                <h2 className={common.pageSubhead}>{language.forgotPassword.linkExpired.subheader}</h2>
            </header>
        </>
    );

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Staff Forgot Password' });
    }, []);

    return (
        <>
            {forgotPasswordStaffUiState === EForgotPasswordStaffUiState.retry ? retryHeader : defaultHeader}
            {forgotPasswordStaffUiState === EForgotPasswordStaffUiState.error && (
                <section className={common.genericError}>
                    <Alert severity="error">{errorMessage}</Alert>
                </section>
            )}
            <form>
                <section className={styles.formContent}>
                    <TextField
                        label={language.common.email}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        error={!emailIsValid}
                        helperText={
                            !emailIsValid && email === '' ? language.validation.emailMissing : language.validation.email
                        }
                        fullWidth
                    />
                </section>
                <section className={styles.formCTA}>
                    <LoadingButton
                        type={'submit'}
                        variant="contained"
                        onClick={handleFormSubmit}
                        disabled={forgotPasswordStaffUiState === EForgotPasswordStaffUiState.loading}
                        loading={forgotPasswordStaffUiState === EForgotPasswordStaffUiState.loading}
                        size="large"
                    >
                        {language.forgotPassword.submitEmailCTA}
                    </LoadingButton>
                </section>
            </form>
        </>
    );
};

const ForgotPasswordStaffSuccess: FC = () => {
    const { language } = useContext(GlobalContext);
    const { email } = useContext(ForgotPasswordStaffContext);
    return (
        <>
            <section className={common.icon}>
                <IconMailWoosh />
            </section>
            <header>
                <h1 className={common.pageHeader}>{language.forgotPassword.success.header}</h1>
            </header>
            <main className={styles.notificationContent}>
                <p className={styles.emailAddress}>{email}</p>
                <p>{language.forgotPassword.success.desc}</p>
            </main>
        </>
    );
};

const ForgotPasswordStaffUi: FC = () => {
    const { forgotPasswordStaffUiState } = useContext(ForgotPasswordStaffContext);
    let forgotPasswordStaffUi: React.ReactElement;
    switch (forgotPasswordStaffUiState) {
        case EForgotPasswordStaffUiState.default:
            forgotPasswordStaffUi = <CardLoader />;
            break;
        case EForgotPasswordStaffUiState.ready:
        case EForgotPasswordStaffUiState.retry:
        case EForgotPasswordStaffUiState.loading:
        case EForgotPasswordStaffUiState.error:
            forgotPasswordStaffUi = <ForgotPasswordStaffForm />;
            break;
        case EForgotPasswordStaffUiState.success:
            forgotPasswordStaffUi = <ForgotPasswordStaffSuccess />;
            break;
        case EForgotPasswordStaffUiState.fatalError:
            forgotPasswordStaffUi = <Redirect to={ERoutes.error} />;
            break;
        default:
            throw new Error('Forgot Password State set incorrectly');
    }
    return forgotPasswordStaffUi;
};

const ForgotPasswordStaffScreen: FC = () => {
    return (
        <ForgotPasswordStaffProvider>
            <PageLayout>
                <Card>
                    <ForgotPasswordStaffUi />
                </Card>
            </PageLayout>
        </ForgotPasswordStaffProvider>
    );
};

export default ForgotPasswordStaffScreen;
